import React, { useRef, useEffect } from "react";
import type { Segment } from "../core/types";
import { drawSegment } from "./SegmentView";

interface TubeViewProps {
  segments: (Segment | null)[];
  isSelected?: boolean;
  width?: number;
  height?: number;
  highlightColor?: string;
}

export default function TubeView({
  segments,
  isSelected = false,
  width   = 45,
  height  = 140,
  highlightColor = "#007bff",
}: TubeViewProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // high-DPR
    canvas.style.width  = `${width}px`;
    canvas.style.height = `${height}px`;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = width  * dpr;
    canvas.height = height * dpr;
    ctx.resetTransform();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const wall   = width * 0.1;            // 10% of width
    const innerW = width - 2 * wall;
    const innerH = height - wall;
    const x0     = wall;
    const y0     = wall / 2;
    const radius = innerW / 2;

    // build U‐shape
    const path = new Path2D();
    path.moveTo(x0, y0);
    path.lineTo(x0, y0 + innerH - radius - y0/2);
    path.arc(
      x0 + innerW/2,
      y0 + innerH - radius - y0/2,
      radius,
      Math.PI, 0, true
    );
    path.lineTo(x0 + innerW, y0);
    path.closePath();

    // clip and draw segments
    ctx.save();
    ctx.clip(path);
    const cap  = segments.length;
    const segH = (innerH - y0) / cap;
    for (let i = 0; i < cap; i++) {
      const slotY = y0 + innerH - (i + 1)*segH;
      drawSegment(ctx, x0, slotY, innerW, segH, segments[i]);
    }

    // dashed separators, using segH so they scale
    ctx.beginPath();
    ctx.setLineDash([segH * 0.2, segH * 0.1]);
    ctx.strokeStyle = "#888";
    ctx.lineWidth   = segH * 0.07;
    for (let i = 1; i < cap; i++) {
      const y = y0 + innerH - i*segH;
      ctx.moveTo(x0 + 2, y);
      ctx.lineTo(x0 + innerW - 2, y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // outline stroke
    ctx.lineWidth   = wall//isSelected ? wall + 2 : wall;
    ctx.strokeStyle = "#444" //isSelected ? highlightColor : "#444";
    ctx.stroke(path);
  }, [segments, isSelected, width, height, highlightColor]);

  return <canvas ref={ref} style={{ cursor: "pointer" }} />;
}
